'use client'

import type { NavigationContentProps } from '@maestro/commerce-blocks/components/navigation-footer/navigation-footer.types'
import type { NavigationItem as NavigationItemType } from '@maestro/data-layer'

import classNames from 'classnames'
import React, { useCallback, useMemo } from 'react'

import NavigationItem from '@maestro/commerce-blocks/components/navigation-footer/navigation-item'
import NewsletterSignup from '@maestro/commerce-blocks/components/newsletter-signup/newsletter-signup'
import { Accordion, AccordionItem } from '@pearlconvert/ui/components/accordion'
import { useBreakpoint } from '@pearlconvert/ui/providers/breakpoint/breakpoint.context'

export default function NavigationContent(props: NavigationContentProps) {
  const { navigation, settings, contactUsCmsBlock } = props
  const { isMobileView } = useBreakpoint()
  const isMobile = isMobileView()

  const navigationList = useCallback((node: NavigationItemType) => {
    if (!node.children?.length) {
      return null
    }

    return (
      <ul className="footer__nav-list" key={node._key}>
        {node.children.map((childNode) => (
          <NavigationItem
            key={childNode._key}
            childNode={childNode}
            sectionTitle={node.title}
          />
        ))}
      </ul>
    )
  }, [])

  const memoizedMobileNavigation = useMemo(() => {
    const nodes = navigation?.children ?? []
    return nodes.map((node, index) => {
      const isContactUs = index === nodes.length - 1
      return (
        <AccordionItem
          title={
            <h3 className={classNames('footer__nav-title', node.cssClass)}>
              {node.title}
            </h3>
          }
          key={node._key}
          location="Footer"
        >
          {isMobile && (
            <>
              {navigationList(node)}
              {isContactUs && (
                <div className="footer__contact">
                  <div>{contactUsCmsBlock}</div>
                </div>
              )}
            </>
          )}
        </AccordionItem>
      )
    })
  }, [isMobile, navigation, navigationList, contactUsCmsBlock])

  const memoizedDesktopNavigation = useMemo(() => {
    const nodes = navigation?.children ?? []
    return nodes.map((node, index) => {
      const isContactUs = index === nodes.length - 1
      return (
        <div className="footer__nav-column desktop-only" key={node._key}>
          <h3 className={classNames('footer__nav-title', node.cssClass)}>
            {node.title}
          </h3>
          {navigationList(node)}
          {isContactUs && (
            <div className="footer__contact">
              <div>{contactUsCmsBlock}</div>
            </div>
          )}
        </div>
      )
    })
  }, [navigation, navigationList, contactUsCmsBlock])

  return (
    <>
      {/*Mobile view*/}
      <div className="mobile-only">
        <Accordion canOpenMultiple>{memoizedMobileNavigation}</Accordion>
        <NewsletterSignup settings={settings} />
      </div>
      {/*Desktop view*/}
      <>
        {memoizedDesktopNavigation}
        <div className="footer__nav-column desktop-only">
          <NewsletterSignup isDesktop={true} settings={settings} />
        </div>
      </>
    </>
  )
}
